import {
  createContext,
  Dispatch,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import Utils from '../../utils/immersive_utils';
import { PortfolioManagementContext } from '../portfolio-management';
import { IAccountOption } from '../venue-provider';
import { WebSocketContext } from '../websocket-context/websocket-provider';

interface IParamsProvider {
  children: ReactNode;
}

interface IProvider {
  orders: any;
  setOrders: (value: any) => void;
  getData: (
    account: IAccountOption | IAccountOption[],
    accountsAggregated?: IAccountOption[],
  ) => void;
  expandableRows: any[];
  setExpandableRows: Dispatch<React.SetStateAction<any[]>>;
  expandableOrdersRows: any[];
  setExpandableOrdersRows: Dispatch<React.SetStateAction<any[]>>;
  isLoadingPositions: boolean;
  isLoadingOrders: boolean;
  isFirstLoadDataPositions: React.MutableRefObject<boolean>;
  isFirstLoadDataOrders: React.MutableRefObject<boolean>;
}

export enum getDataType {
  positions_deribit = 'load_deribit_trades',
  orders_deribit = 'deribit_open_orders',
  positions_okx = 'okx_positions',
  orders_okx = 'okx_open_orders',
  positions_otc = 'positions',
  orders_otc = 'open_orders',
}

const POSITIONS_ID = 'POSITIONS_KEY';
const ORDERS_ID = 'ORDERS_KEY';

export const PositionsContext = createContext<IProvider>({} as IProvider);

export const PositionsProvider = (props: IParamsProvider) => {
  const { sendMessage, subscribeToChannel } = useContext(WebSocketContext);
  const { addDataToAccount, addGeneralAggregatedData } = useContext(PortfolioManagementContext);
  const [orders, setOrders] = useState<any>(null);
  const [expandableRows, setExpandableRows] = useState<any[]>([]);
  const [expandableOrdersRows, setExpandableOrdersRows] = useState<any[]>([]);
  const [isLoadingPositions, setIsLoadingPositions] = useState(false);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [counterAccountsAggregatedPositions, setCounterAccountsAggregatedPositions] = useState<any>(
    {
      POSTIONS: 0,
      ORDERS: 0,
    },
  );
  const isFirstLoadDataPositions = useRef<boolean>(true);
  const isFirstLoadDataOrders = useRef<boolean>(true);

  const handleResponse = (response: any, account: IAccountOption | IAccountOption[]) => {
    if (!response) return;

    const processAccountData = (
      account: IAccountOption,
      data: any,
      error: boolean,
      dataParent: any,
    ) => {
      addDataToAccount({
        account,
        dataParent,
        data: !error ? Utils.csvToObject(data) : [],
        error,
      });

      if (dataParent === 'POSTIONS') {
        setIsLoadingPositions(false);
      } else if (dataParent === 'ORDERS') {
        setIsLoadingOrders(false);
      }
    };

    const processAggregatedAccountsData = (
      data: any,
      error: boolean,
      exchangeParam: string,
      dataParent: any,
    ) => {
      addGeneralAggregatedData({
        dataParent,
        data: !error ? Utils.csvToObject(data) : [],
        error,
        exchange: exchangeParam,
      });

      setCounterAccountsAggregatedPositions((state: any) => ({
        ...state,
        [dataParent]: state[dataParent] + 1,
      }));
    };

    const positionsIdKey = `${Array.isArray(account) ? account[0].exchange.toUpperCase() : account.exchange.toUpperCase()}_${POSITIONS_ID}`;
    const ordersIdKey = `${Array.isArray(account) ? account[0].exchange.toUpperCase() : account.exchange.toUpperCase()}_${ORDERS_ID}`;

    const handleSuccessResponse = (dataParent: any) => {
      const isError = false;

      if (Array.isArray(account)) {
        processAggregatedAccountsData(response.result, isError, account[0].exchange, dataParent);
      } else {
        let getResult;

        if (account.exchange === 'otc' || account.exchange === 'binance') {
          getResult = response.result.positions;
        } else if (
          account.exchange === 'bullish' ||
          account.exchange === 'coincall' ||
          account.exchange === 'bybit'
        ) {
          getResult =
            dataParent === 'ORDERS' ? response.result.open_orders : response.result.positions;
        } else {
          getResult = response.result;
        }

        processAccountData(account, getResult, isError, dataParent);
      }
    };

    const handleErrorResponse = (dataParent: any) => {
      const isError = true;

      if (response?.error) {
        if (Array.isArray(account)) {
          processAggregatedAccountsData([], isError, account[0].exchange, dataParent);
        } else {
          processAccountData(account, [], isError, dataParent);
        }
      }
    };

    if (response.result) {
      if (response?.id === positionsIdKey) {
        handleSuccessResponse('POSTIONS');
      } else if (response?.id === ordersIdKey) {
        handleSuccessResponse('ORDERS');
      }
    } else {
      if (response?.id === positionsIdKey) {
        handleErrorResponse('POSTIONS');
      } else if (response?.id === ordersIdKey) {
        handleErrorResponse('ORDERS');
      }
    }
  };

  const getData = useCallback(
    async (accounts: IAccountOption | IAccountOption[], accountsAggregated?: IAccountOption[]) => {
      const makeGetData = (
        accountParam: IAccountOption | IAccountOption[],
        type: getDataType | string,
        id: string,
      ) => {
        let credentials;

        if (!Array.isArray(accountParam)) {
          if (accountParam.exchange === 'otc') {
            credentials = { venue: accountParam.venue, account: accountParam.name };
          } else {
            credentials = { name: accountParam.name };
          }
        } else {
          credentials = accountParam.map((account) => ({ name: account.name }));
        }

        const params = {
          jsonrpc: '2.0',
          id: id,
          method: type,
          params: {
            credentials,
          },
        };

        const hasDeritData = accountsAggregated?.filter(
          (accAgg: any) => accAgg.exchange === 'deribit',
        );
        const hasOkxData = accountsAggregated?.filter((accAgg: any) => accAgg.exchange === 'okx');
        const hasBothAccountsAggregated =
          hasDeritData && hasDeritData?.length > 0 && hasOkxData && hasOkxData.length > 0;

        const isVerifyAccountsAggregated =
          (hasDeritData && hasDeritData.length > 0) || (hasOkxData && hasOkxData.length > 0);

        if (!hasBothAccountsAggregated) {
          if (isVerifyAccountsAggregated) {
            const counterKey = id.includes(POSITIONS_ID) ? 'POSTIONS' : 'ORDERS';

            setCounterAccountsAggregatedPositions((state: any) => ({
              ...state,
              [counterKey]: state[counterKey] + 1,
            }));
          }
        }

        if (id.includes(POSITIONS_ID)) {
          setIsLoadingPositions(true);
        } else {
          setIsLoadingOrders(true);
        }
        sendMessage(JSON.stringify(params));
        subscribeToChannel(id, (response: any) => handleResponse(response, accounts));
      };

      if (Array.isArray(accounts)) {
        const exchange = accounts[0].exchange;
        if (exchange === 'okx') {
          makeGetData(accounts, getDataType.positions_okx, 'OKX_' + POSITIONS_ID);
          makeGetData(accounts, getDataType.orders_okx, 'OKX_' + ORDERS_ID);
        } else if (exchange === 'deribit') {
          makeGetData(accounts, getDataType.positions_deribit, 'DERIBIT_' + POSITIONS_ID);
          makeGetData(accounts, getDataType.orders_deribit, 'DERIBIT_' + ORDERS_ID);
        }
      } else {
        if (accounts.exchange === 'binance') {
          makeGetData(accounts, 'positions', 'BINANCE_' + POSITIONS_ID);
        } else if (accounts.exchange === 'bullish') {
          makeGetData(accounts, 'positions', 'BULLISH_' + POSITIONS_ID);
          makeGetData(accounts, 'open_orders', 'BULLISH_' + ORDERS_ID);
        } else if (accounts.exchange === 'bybit') {
          makeGetData(accounts, 'positions', 'BYBIT_' + POSITIONS_ID);
          makeGetData(accounts, 'open_orders', 'BYBIT_' + ORDERS_ID);
        } else if (accounts.exchange === 'coincall') {
          makeGetData(accounts, 'positions', 'COINCALL_' + POSITIONS_ID);
          makeGetData(accounts, 'open_orders', 'COINCALL_' + ORDERS_ID);
        } else if (accounts.exchange === 'deribit') {
          makeGetData(accounts, getDataType.positions_deribit, 'DERIBIT_' + POSITIONS_ID);
          makeGetData(accounts, getDataType.orders_deribit, 'DERIBIT_' + ORDERS_ID);
        } else if (accounts.exchange === 'okx') {
          makeGetData(accounts, getDataType.positions_okx, 'OKX_' + POSITIONS_ID);
          makeGetData(accounts, getDataType.orders_okx, 'OKX_' + ORDERS_ID);
        } else if (accounts.exchange === 'otc') {
          makeGetData(accounts, getDataType.positions_otc, 'OTC_' + POSITIONS_ID);
        }
      }
    },
    [sendMessage, counterAccountsAggregatedPositions],
  );

  useEffect(() => {
    if (counterAccountsAggregatedPositions.POSTIONS === 2) {
      setCounterAccountsAggregatedPositions((state: any) => ({
        ...state,
        POSTIONS: 0,
      }));
      setIsLoadingPositions(false);
    }

    if (counterAccountsAggregatedPositions.ORDERS === 2) {
      setCounterAccountsAggregatedPositions((state: any) => ({
        ...state,
        ORDERS: 0,
      }));
      setIsLoadingOrders(false);
    }
  }, [counterAccountsAggregatedPositions, getData]);

  return (
    <PositionsContext.Provider
      value={{
        orders,
        setOrders,
        getData,
        expandableRows,
        expandableOrdersRows,
        setExpandableRows,
        setExpandableOrdersRows,
        isLoadingPositions,
        isLoadingOrders,
        isFirstLoadDataPositions,
        isFirstLoadDataOrders,
      }}
    >
      {props.children}
    </PositionsContext.Provider>
  );
};
